import { useEffect, useState } from 'react';
import { BookOpenText, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGenerateExplain, useGenerateExplainSimple } from '../../api/queries';
import sumStyles from '../summary/SummaryView.module.css';

type Mode = 'detailed' | 'simple';

export function ExplainView() {
  const [mode, setMode] = useState<Mode>('detailed');
  const detailed = useGenerateExplain();
  const simple = useGenerateExplainSimple();

  useEffect(() => {
    if (mode === 'detailed' && !detailed.data && !detailed.isPending && !detailed.error) {
      detailed.mutate({});
    }
    if (mode === 'simple' && !simple.data && !simple.isPending && !simple.error) {
      simple.mutate({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className={sumStyles.view}>
      <div className={sumStyles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'detailed'}
          className={mode === 'detailed' ? sumStyles.tabActive : sumStyles.tab}
          onClick={() => setMode('detailed')}
        >
          <BookOpenText size={14} strokeWidth={2.2} /> Detallada
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'simple'}
          className={mode === 'simple' ? sumStyles.tabActive : sumStyles.tab}
          onClick={() => setMode('simple')}
        >
          <Sparkles size={14} strokeWidth={2.2} /> Sencilla
        </button>
      </div>

      <div className={sumStyles.body}>
        {mode === 'detailed' ? (
          <DetailedExplanation mut={detailed} />
        ) : (
          <SimpleExplanation mut={simple} />
        )}
      </div>
    </div>
  );
}

function DetailedExplanation({
  mut
}: {
  mut: ReturnType<typeof useGenerateExplain>;
}) {
  if (mut.isPending)
    return <LoadingState label="Preparando una explicación detallada…" />;
  if (mut.error)
    return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;
  const raw = data.content?.markdown ?? data.content?.explanation;
  const md = typeof raw === 'string' ? raw : detailedToMarkdown(raw);
  return (
    <article className={sumStyles.markdown}>
      <ReactMarkdown>{md}</ReactMarkdown>
    </article>
  );
}

function SimpleExplanation({
  mut
}: {
  mut: ReturnType<typeof useGenerateExplainSimple>;
}) {
  if (mut.isPending)
    return <LoadingState label="Simplificando el contenido…" hint="Reescribiéndolo en lenguaje claro" />;
  if (mut.error)
    return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;
  const md = simpleToMarkdown(data.content);
  return (
    <article className={sumStyles.markdown}>
      <ReactMarkdown>{md}</ReactMarkdown>
    </article>
  );
}

// ── Markdown flatteners ─────────────────────────────────────────────────────

// /activity/explain returns content.explanation as a structured object:
//   core_concept { simple_definition, main_analogy { comparison, explanation } }
//   progressive_structure { main_idea, relationships, key_components[] }
//   summary, examples[]
function detailedToMarkdown(exp: any): string {
  if (!exp || typeof exp !== 'object') return '';
  const out: string[] = [];

  const core = exp.core_concept;
  if (core?.simple_definition) out.push('## En pocas palabras', core.simple_definition);
  if (core?.main_analogy?.comparison || core?.main_analogy?.explanation) {
    out.push('## Analogía');
    if (core.main_analogy.comparison) out.push(`**${core.main_analogy.comparison}**`);
    if (core.main_analogy.explanation) out.push(core.main_analogy.explanation);
  }

  const prog = exp.progressive_structure;
  if (prog?.main_idea) out.push('## Idea principal', prog.main_idea);
  if (Array.isArray(prog?.key_components) && prog.key_components.length) {
    out.push('## Componentes clave');
    for (const c of prog.key_components) {
      if (c?.component) out.push(`### ${c.component}`);
      if (c?.explanation) out.push(c.explanation);
    }
  }
  if (prog?.relationships) out.push('## Relaciones entre piezas', prog.relationships);

  if (Array.isArray(exp.examples) && exp.examples.length) {
    out.push('## Ejemplos');
    for (const ex of exp.examples) {
      if (ex?.scenario) out.push(`### ${ex.scenario}`);
      if (ex?.relevance) out.push(ex.relevance);
    }
  }

  if (exp.summary) out.push('## Resumen', exp.summary);

  return out.join('\n\n');
}

// /activity/explain-simple returns content with these markdown-string fields:
//   core_concept, plain_language, real_world_example, visual_analogy
// plus structured arrays:
//   alternative_terms[{ technical_term, simple_alternative }]
//   step_by_step[]   (already numbered markdown strings)
function simpleToMarkdown(content: any): string {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (typeof content.markdown === 'string') return content.markdown;

  const out: string[] = [];
  if (content.core_concept) out.push(String(content.core_concept));
  if (content.plain_language) out.push(String(content.plain_language));
  if (content.real_world_example) out.push(String(content.real_world_example));
  if (content.visual_analogy) out.push(String(content.visual_analogy));

  if (Array.isArray(content.step_by_step) && content.step_by_step.length) {
    out.push('# Paso a paso');
    out.push(content.step_by_step.map((s: string) => String(s)).join('\n'));
  }

  if (
    Array.isArray(content.alternative_terms) &&
    content.alternative_terms.length
  ) {
    out.push('# Términos equivalentes');
    for (const t of content.alternative_terms) {
      if (t?.technical_term || t?.simple_alternative) {
        out.push(
          `- **${t.technical_term ?? ''}** → ${t.simple_alternative ?? ''}`
        );
      }
    }
  }

  return out.join('\n\n');
}
